import {
  assembleTrackingCode,
  formatPartDate,
  partPlaceholder,
} from '@inference.engine/taxonomy/web';
import { patternText, type TaxonomyField, type TrackingCodePart } from '@/entities/taxonomy';

export function patternToString(pattern: TrackingCodePart[], fields: TaxonomyField[]): string {
  const nameById = new Map(fields.map((f) => [f.id, f.name]));
  return patternText(pattern, (id) => nameById.get(id));
}

/**
 * Resolves a computed field's pattern for ONE builder row — the live preview of
 * the generated value using the schema's own rules: field parts emit the
 * referenced field's code (or value) at that row, date parts the current date,
 * and the sequence a `#` placeholder of its length (the real unique number is allocated
 * server-side at generation, not per vocabulary row). Returns '' when the row
 * has nothing to resolve.
 */
export function resolveVariableForRow(
  field: TaxonomyField,
  fields: TaxonomyField[],
  row: number,
): string {
  const pattern = field.pattern ?? [];
  if (pattern.length === 0) return '';
  const byId = new Map(fields.map((f) => [f.id, f]));
  let hasResolvedPart = false;
  const out = pattern
    .map((part) => {
      switch (part.kind) {
        case 'literal':
          return part.text ?? '';
        case 'date':
          hasResolvedPart = true;
          // The generator's own renderer, so a cleared mask previews the empty
          // string it will actually mint. Routing this through the FIELD helper
          // was wrong by eight characters: that one falls back on blank as well
          // as on unset, because a date picker cannot render nothing.
          return formatPartDate(new Date(), part.format);
        case 'sequence':
        case 'random':
          return partPlaceholder(part);
        case 'field': {
          const ref = part.fieldId ? byId.get(part.fieldId) : undefined;
          const v = ref?.values[row];
          if (!v || (!v.code && !v.value)) return '';
          hasResolvedPart = true;
          return part.source === 'value' ? v.value : v.code || v.value;
        }
        default:
          return '';
      }
    })
    .join('');
  // Only show a preview once at least one field/date part has real data for the row.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- the flag is set inside the map callback, which TS's flow analysis does not follow
  return hasResolvedPart ? out : '';
}

/**
 * The code a mint would build from each referenced field's first value, with the sequence and
 * random parts left as placeholders. Unlike {@link resolveVariableForRow} it is never blank for a
 * pattern of literals and a sequence, because a mint is not either.
 */
export function sampleTrackingCode(field: TaxonomyField, fields: TaxonomyField[]): string {
  const firstValues = Object.fromEntries(fields.map((f) => [f.id, f.values[0]?.value ?? '']));
  return assembleTrackingCode(field, fields, firstValues, { now: new Date() });
}

/** Whether a pattern holds a part whose characters are only filled in when a code is minted. */
export function hasPlaceholderPart(pattern: readonly TrackingCodePart[]): boolean {
  return pattern.some((p) => p.kind === 'sequence' || p.kind === 'random');
}
